import React from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { Device } from "@/types/device";

interface OSDistributionChartProps {
  devices: Device[];
}

const COLORS = ["#6366F1", "#4ADE80", "#FF8C66", "#A5B4FC", "#FFBB80", "#86EFAC", "#F472B6"];

export function OSDistributionChart({ devices }: OSDistributionChartProps) {
  const osCounts: Record<string, number> = {};
  devices.forEach((d) => {
    const os = (d.os_type || "unknown").toLowerCase();
    const label = os === "ios" ? "iOS" : os === "macos" ? "macOS" : os === "tvos" ? "tvOS" : os.charAt(0).toUpperCase() + os.slice(1);
    osCounts[label] = (osCounts[label] || 0) + 1;
  });

  const data = Object.entries(osCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const total = data.reduce((s, d) => s + d.value, 0);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No device data available
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover text-popover-foreground p-3 shadow-md rounded-md border">
          <p className="font-medium text-sm">{payload[0].name}</p>
          <p className="text-sm">{`${payload[0].value} device${payload[0].value !== 1 ? "s" : ""}`}</p>
          <p className="text-xs text-muted-foreground">{`${Math.round((payload[0].value / total) * 100)}%`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={90}
          paddingAngle={3}
          dataKey="value"
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
          style={{ fontSize: 11 }}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={COLORS[i % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}
